import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Message} from "primeng/api";
import {IFlow} from "../../../../../data/interfaces/iflow.interface";
import {ApiFlowDataService} from "../../../../../data/services/api/flow/api-flow-data.service";
import * as CapacitorCore from "@capacitor/core";
import * as CapacitorBrowser from "@capacitor/browser";
import {WorkerService} from "../../../../../data/services/api/worker.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {BANKS, DRUO_BANKS} from '../../data/constants/druo-banks.constant';
import {IBankDruo} from "../../data/interfaces/iexpress-league";
import {clabeBankSupportedValidator, clabeFormatValidator} from "../../data/validators/clabe.validators";
import {Subject, takeUntil} from "rxjs";
import {
  faBuildingColumns,
  faCheck,
  faChevronDown,
  faCircleExclamation,
  faClock,
  faIdCard,
  faMagnifyingGlass,
  faPen,
  faPlus,
  faRotateRight,
  faShieldHalved,
  faWallet,
  faXmark,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {ESTADOS_LX} from "../../../../../shared/ui/liga-ui";
import {ITonoMensajeLx, porMensajeLx, tonoMensajeLx} from "../../../../../shared/ui/liga-mensajes";
import {
  clabeDigitoControlValidator,
  clabeLongitudValidator,
  formatearClabe,
  LONGITUD_CLABE,
  nombreBancoLegible,
  normalizarBusqueda,
  posicionTrasDigitos,
  soloDigitos
} from "./clabe-formato";

const BANK_STATEMENT_DOC_ID = '65b705565ba06c362d867b8e';
const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;

// Chip de las cuentas ya registradas (mismo criterio que el p-tag de antes).
interface IEstadoCuenta {
  texto: string
  chip: string
  icono: IconDefinition
}

const CUENTA_CONECTADA: IEstadoCuenta = {texto: 'Conectada', chip: 'lx-chip--listo', icono: faCheck}
const CUENTA_VALIDADA: IEstadoCuenta = {texto: 'Validada', chip: 'lx-chip--listo', icono: faCheck}
const CUENTA_EN_VERIFICACION: IEstadoCuenta = {texto: 'En verificación', chip: 'lx-chip--revision', icono: faClock}

// Los estilos viven en src/assets/styles/components/_liga-ui.scss (.lx-*) y
// _liga-c.scss (.lxc-*): el build de producción corta en 4 kB por componente.
@Component({
  selector: 'app-account-validation-component',
  templateUrl: './account-validation-component.component.html',
  styleUrls: ['./account-validation-component.component.scss']
})
export class AccountValidationComponentComponent  implements OnInit, OnDestroy {
  responseMessage: Message[] = []
  showMessage = false
  startButtonIsAvailable = true
  public form!: FormGroup<{ clabe: FormControl<string> }>;
  public detectedBank: IBankDruo | null = null;
  private destroy$ = new Subject<void>();
  allBanks: IBankDruo[] = BANKS;
  filteredBanks: string[] = this.allBanks.map(b => b.banco);
  otpControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^\d{2}$/)
  ]);
  rfcControl = new FormControl('', [
    Validators.required,
    Validators.pattern(RFC_REGEX)
  ]);
  private messageTimer?: any;
  private rfcValue = '';
  rfcIsEditing = false;
  rfcConfirmed = false;
  step: 'EXISTING' | 'CLABE' | 'OTP' | 'CONFIRM' = 'CLABE';
  existingAccounts: any[] = [];
  otpRequired = false;
  slug = '';
  account_uuid = '';
  workerId = '';
  clabe = '';
  @Input() data!: IFlow
  @Output() onEndProcess = new EventEmitter()
  @Output() onUpdateStage = new EventEmitter()
  startedProcess = false
  otpAttempts = 0;
  maxOtpAttempts = 3;
  otpBlocked = false;
  otpBlockTimer = 0;
  private timerInterval?: any;

  // ---------- Solo presentación (estados dentro de la tarjeta) ----------
  // Sin LoaderService: su capa de carga tapaba la pantalla y se quedaba 3 s de más;
  // ahora cada espera vive en su tarjeta o en su botón.
  cargandoInicial = true
  errorInicial = false
  cargandoRfc = false
  enCurso: '' | 'usar' | 'conectar' | 'otp' | 'confirmar' = ''
  terminado = false
  intentoValidar = false
  cuentaElegida = 0
  mostrarBancos = false
  busquedaBanco = ''
  readonly bancosLegibles: string[] = this.filteredBanks.map(b => nombreBancoLegible(b))
  bancosVisibles: string[] = this.bancosLegibles
  readonly longitudClabe = LONGITUD_CLABE
  readonly segmentos = [1, 2, 3]
  // Nombres cortos: caben a 320 px en las tres partes del encabezado.
  readonly nombresPaso = ['CLABE', 'Centavos', 'Confirma']
  readonly intentos = [1, 2, 3]
  readonly estados = ESTADOS_LX
  readonly porMensaje = porMensajeLx
  readonly iconos = {
    banco: faBuildingColumns,
    cartera: faWallet,
    rfc: faIdCard,
    listo: faCheck,
    error: faCircleExclamation,
    reloj: faClock,
    editar: faPen,
    agregar: faPlus,
    buscar: faMagnifyingGlass,
    chevron: faChevronDown,
    reintentar: faRotateRight,
    seguro: faShieldHalved,
    cerrar: faXmark
  }

  constructor(
    private flowDataService: ApiFlowDataService,
    private workerService: WorkerService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {
  }
  ngOnInit() {
    this.cargandoInicial = true;
    this.workerService.getWorkerId().subscribe({
      next: (workerRes) => {
        if (workerRes.error || !workerRes.data?.workerId) {
          this.cargandoInicial = false;
          this.errorInicial = true;
          this.responseMessage = [{
            summary: workerRes.msg || 'No se pudo obtener el ID del trabajador',
            severity: 'error',
            closable: false
          }];
          return;
        }
        this.workerId = workerRes.data.workerId;
        this.checkExistingAccounts();
      },
      error: () => {
        this.cargandoInicial = false;
        this.errorInicial = true;
        this.responseMessage = [{
          summary: 'Error obteniendo el ID del trabajador',
          severity: 'error',
          closable: false
        }];
      }
    });
    this.form = this.fb.nonNullable.group({
      clabe: this.fb.nonNullable.control('', {
        // Los dos últimos se suman en cliente: 18 dígitos exactos y dígito de control.
        validators: [Validators.required, clabeFormatValidator(), clabeBankSupportedValidator(BANKS), clabeLongitudValidator(), clabeDigitoControlValidator()],
        updateOn: 'change'
      })
    });
    this.clabeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const normalized = value.replace(/\D+/g, '');
        if (normalized !== value) {
          this.clabeControl.setValue(normalized, { emitEvent: false });
        }

        if (normalized.length >= 3) {
          const prefix = normalized.substring(0, 3);
          this.detectedBank = BANKS.find(b => b.digito === prefix) ?? null;
        } else {
          this.detectedBank = null;
        }
      });

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
  }

  public get clabeControl(): FormControl<string> {
    return this.form.controls.clabe;
  }

  public get clabeHasError(): boolean {
    const c = this.clabeControl;
    return c.invalid && (c.dirty || c.touched);
  }

  public get isStartDisabled(): boolean {
    return !this.rfcConfirmed || this.clabeControl.invalid || !this.startButtonIsAvailable;
  }


  private loadRfcFromStatement() {
    this.cargandoRfc = true;
    this.flowDataService.getRfc(this.workerId, BANK_STATEMENT_DOC_ID).subscribe({
      next: (res) => {
        this.cargandoRfc = false;
        this.cargandoInicial = false;

        if (res?.error) {
          this.responseMessage = [{
            summary: res.msg || 'No fue posible cargar el RFC del estado de cuenta.',
            severity: 'warn',
            closable: false
          }];
          return;
        }

        const rfc = String(res?.data?.rfc ?? '').trim();
        if (rfc && RFC_REGEX.test(rfc)) {
          this.rfcValue = rfc.toUpperCase();
          this.rfcControl.setValue(this.rfcValue);
          this.rfcControl.markAsPristine();
          this.rfcControl.markAsUntouched();
          this.rfcIsEditing = false;
          this.rfcConfirmed = false;
        } else {
          this.rfcIsEditing = true;
          this.rfcConfirmed = false;
          this.responseMessage = [{
            summary: 'No encontramos un RFC válido en tu estado de cuenta. Escríbelo tal como aparece ahí y guárdalo.',
            severity: 'warn',
            closable: true
          }];
        }
      },
      error: () => {
        this.cargandoRfc = false;
        this.cargandoInicial = false;
        this.responseMessage = [{
          summary: 'No fue posible cargar el RFC del estado de cuenta.',
          severity: 'error',
          closable: false
        }];
      }
    });
  }

  get showRfcActionButton(): boolean {
    return this.rfcIsEditing || !this.rfcConfirmed;
  }


  get rfcPrimaryLabel(): string {
    return this.rfcIsEditing ? 'Guardar y continuar' : 'Confirmar';
  }

  enableRfcEdit() {
    this.rfcIsEditing = true;
    this.rfcConfirmed = false;
  }

  confirmRfcAndContinue() {
    if (this.rfcControl.invalid) {
      this.rfcControl.markAsTouched();
      return;
    }

    const veniaDeEditar = this.rfcIsEditing;
    this.rfcValue = String(this.rfcControl.value).toUpperCase();

    this.rfcIsEditing = false;
    this.rfcConfirmed = true;

    this.responseMessage = [{
      summary: veniaDeEditar ? 'Guardamos tu RFC. Ya puedes validar tu cuenta.' : 'Confirmaste tu RFC. Ya puedes validar tu cuenta.',
      severity: 'success',
      closable: false
    }];
  }

  confirmCompletedProcess() {
    this.enCurso = 'confirmar';
    this.flowDataService.updateSessionStatusAndStage(this.workerId, this.data.properties, this.clabe).subscribe(r => {
      this.enCurso = '';

      this.responseMessage = [{
        summary: r.msg,
        severity: r.error ? 'error' : 'success',
        closable: false,
      }];

      if (!r.error) {
        this.terminado = true;
        setTimeout(() => this.terminar(), 1500);
      } else {
        this.onUpdateStage.emit();
      }
    });
  }

  private checkExistingAccounts() {
    this.flowDataService.getWorkerAccounts(this.workerId).subscribe({
      next: (res) => {
        if (!res.error && res.data && res.data.length > 0) {
          this.existingAccounts = res.data;
          this.step = 'EXISTING';
          this.cargandoInicial = false;
        } else {
          this.loadRfcFromStatement();
        }
      },
      error: () => {
        this.loadRfcFromStatement();
      }
    });
  }

  useExistingAccount(account: any) {
    this.clabe = account.accountNumber;
    this.enCurso = 'usar';
    this.flowDataService.updateSessionStatusAndStage(this.workerId, this.data.properties, this.clabe).subscribe(r => {
      this.enCurso = '';

      this.responseMessage = [{
        summary: r.msg,
        severity: r.error ? 'error' : 'success',
        closable: false,
      }];

      if (!r.error) {
        this.terminado = true;
        setTimeout(() => this.terminar(), 1500);
      } else {
        this.onUpdateStage.emit();
      }
    });
  }

  registerNewAccount() {
    this.step = 'CLABE';
    this.responseMessage = [];
    this.loadRfcFromStatement();
  }

  start() {
    if (this.clabeControl.invalid) {
      this.clabeControl.markAsTouched();
      return;
    }
    if (!this.rfcConfirmed) {
      this.responseMessage = [{
        summary: 'Confirma tu RFC para continuar.',
        severity: 'warn',
        closable: true
      }];
      return;
    }

    this.enCurso = 'conectar';

    this.clabe = String(this.clabeControl.value);
    const body = {
      workerId: this.workerId,
      accountNumber: this.clabe,
      rfc: this.rfcValue
    };

      this.flowDataService.connectAccount(body).subscribe(r => {
        this.enCurso = '';

        if (!r.error) {
          this.slug = String(r.data.slug);
          this.account_uuid = String(r.data.account_uuid);
          this.otpRequired = true;
          this.startButtonIsAvailable = false;
          this.responseMessage = [{
            summary: 'Registramos tu cuenta. Ahora escribe los centavos del microdepósito que te enviamos.',
            severity: 'success',
            closable: false
          }];
          this.step = 'OTP';
        } else {
          this.responseMessage = [{
            summary: r.msg,
            severity: 'error',
            closable: false
          }];
        }
      });
  }

  validateOtp() {
    if (this.otpControl.invalid) {
      this.otpControl.markAsTouched();
      return;
    }

    if (this.otpBlocked) {
      return;
    }

    const otp = Number(this.otpControl.value);
    this.enCurso = 'otp';

    this.flowDataService.authenticateAccount({
      slug: this.slug,
      account_uuid: this.account_uuid,
      otp
    }).subscribe(res => {
      this.enCurso = '';


      if (!res.error) {
        this.responseMessage = [{
          summary: res.msg,
          severity: res.error ? 'error' : 'success',
          closable: false
        }];

        this.step = 'CONFIRM';
        this.startedProcess = true;
        this.stopOtpTimer();
      } else {
        this.otpAttempts++;

        if (this.otpAttempts >= this.maxOtpAttempts) {
          this.responseMessage = [{
            summary: 'Se acabaron los intentos. Vuelve a validar tu cuenta para recibir un microdepósito nuevo.',
            severity: 'error',
            closable: false
          }];
          setTimeout(() => {
            this.resetOtpProcess();
          }, 3000);
        } else {
          this.startOtpBlockTimer();
        }
      }
    });
  }

  onClabePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanedText = pastedText.replace(/\s+/g, '');
    this.clabeControl.setValue(cleanedText);
  }

  // El aviso no lleva los segundos: así no cambia (ni se vuelve a anunciar) cada
  // segundo; si hay espera, la cuenta regresiva va en el botón.
  private mensajeOtpIncorrecto(restantes: number): string {
    return `El monto no coincide. Te quedan ${restantes} intento${restantes > 1 ? 's' : ''}.`;
  }

  private startOtpBlockTimer() {
    this.otpBlocked = true;
    this.otpBlockTimer = 30;

    this.stopOtpTimer();

    const remainingAttempts = this.maxOtpAttempts - this.otpAttempts;
    this.responseMessage = [{
      summary: this.mensajeOtpIncorrecto(remainingAttempts),
      severity: 'warn',
      closable: false
    }];

    this.cdr.detectChanges();

    this.timerInterval = setInterval(() => {
      this.otpBlockTimer--;

      if (this.otpBlockTimer <= 0) {
        this.stopOtpTimer();
        this.responseMessage = [{
          summary: `El monto no coincide. Revisa el depósito e inténtalo de nuevo: te quedan ${this.maxOtpAttempts - this.otpAttempts} intento${(this.maxOtpAttempts - this.otpAttempts) > 1 ? 's' : ''}.`,
          severity: 'warn',
          closable: false
        }];
        this.cdr.detectChanges();
      } else {
        const currentRemaining = this.maxOtpAttempts - this.otpAttempts;
        this.responseMessage = [{
          summary: this.mensajeOtpIncorrecto(currentRemaining),
          severity: 'warn',
          closable: false
        }];
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  private stopOtpTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    this.otpBlocked = false;
    this.otpBlockTimer = 0;
    this.cdr.detectChanges();
  }

  private resetOtpProcess() {
    this.stopOtpTimer();
    this.otpAttempts = 0;
    this.otpControl.reset();
    this.step = 'CLABE';
    this.startButtonIsAvailable = true;
    this.otpRequired = false;
  }

  get remainingOtpAttempts(): number {
    return this.maxOtpAttempts - this.otpAttempts;
  }

  get isOtpSubmitDisabled(): boolean {
    return this.otpControl.invalid || this.otpBlocked;
  }

  // ---------- Lectura para la plantilla (sin lógica de negocio) ----------

  // Un solo onEndProcess(true): el regreso automático y «Continuar» de la
  // celebración pasan por aquí (mismo evento, mismos tiempos de antes).
  private finEmitido = false
  terminar() {
    if (this.finEmitido) return
    this.finEmitido = true
    this.onEndProcess.emit(true)
  }

  get pasoActual(): number {
    if (this.step === 'OTP') return 2;
    if (this.step === 'CONFIRM') return 3;
    return 1;
  }

  // CTA fijo abajo solo en la pantalla larga (CLABE + RFC).
  get conCtaFijo(): boolean {
    return !this.cargandoInicial && !this.errorInicial && this.step === 'CLABE';
  }

  get clabeVisible(): string {
    return formatearClabe(this.clabeControl?.value);
  }

  get digitosClabe(): number {
    return soloDigitos(this.clabeControl?.value).length;
  }

  get clabeCompleta(): boolean {
    return !!this.clabeControl && this.clabeControl.valid && this.digitosClabe === LONGITUD_CLABE;
  }

  get bancoDetectado(): string {
    return this.detectedBank ? nombreBancoLegible(this.detectedBank.banco) : '';
  }

  // Un solo mensaje a la vez, el más útil primero. Lo que no puede esperar
  // (banco no compatible, letras, de más) sale al escribir; lo demás al salir del campo.
  get errorClabe(): string {
    const c = this.clabeControl;
    const e = c?.errors;
    if (!e) return '';
    const inmediato = !!(e['clabeBankUnsupported'] || e['clabeTooLong'] || e['clabeInvalidChars']);
    if (!c.touched && !inmediato) return '';
    const n = this.digitosClabe;
    if (e['required']) return 'Escribe tu CLABE de 18 dígitos.';
    if (e['clabeInvalidChars']) return 'Tu CLABE solo lleva números.';
    if (e['clabeBankUnsupported']) return `El banco de esta CLABE (clave ${soloDigitos(c.value).slice(0, 3)}) aún no es compatible con DRUO. Revisa la lista de bancos o usa otra cuenta.`;
    if (e['clabeTooLong']) return `La CLABE tiene 18 dígitos y escribiste ${n}. Revisa que no sobre ninguno.`;
    if (e['clabeLongitud'] || e['clabeTooShortForBank']) {
      const faltan = LONGITUD_CLABE - n;
      return faltan === 1 ? 'Te falta 1 dígito: la CLABE lleva 18.' : `Te faltan ${faltan} dígitos: la CLABE lleva 18.`;
    }
    if (e['clabeDigitoControl']) return 'Algún dígito no coincide. Revisa tu CLABE o cópiala tal cual de la app de tu banco.';
    return 'Revisa tu CLABE.';
  }

  get modoRfc(): 'cargando' | 'confirmado' | 'editando' | 'detectado' {
    if (this.cargandoRfc) return 'cargando';
    if (this.rfcConfirmed && !this.rfcIsEditing) return 'confirmado';
    if (this.rfcIsEditing || !this.rfcControl.value) return 'editando';
    return 'detectado';
  }

  get rfcVisible(): string {
    return String(this.rfcControl.value ?? '').toUpperCase();
  }

  get errorRfc(): string {
    const c = this.rfcControl;
    if (!c.invalid || !(c.touched || this.intentoValidar)) return '';
    if (c.errors?.['required']) return 'Escribe tu RFC.';
    return 'Revisa tu RFC: lleva 3 o 4 letras, 6 números de tu fecha de nacimiento y 3 caracteres de homoclave.';
  }

  get errorOtp(): string {
    const c = this.otpControl;
    if (!c.invalid || !c.touched) return '';
    if (c.errors?.['required']) return 'Escribe los 2 dígitos de los centavos.';
    return 'Escribe 2 números, por ejemplo 05 o 37.';
  }

  get pendienteRfc(): boolean {
    return this.intentoValidar && !this.rfcConfirmed && !this.cargandoRfc;
  }

  estadoCuenta(account: any): IEstadoCuenta {
    if (account?.accountStatus === 'CONNECTED' || account?.previousAccountStatus === 'CONNECTED') return CUENTA_CONECTADA;
    if (account?.accountStatus === 'VALIDATED') return CUENTA_VALIDADA;
    return CUENTA_EN_VERIFICACION;
  }

  nombreBanco(nombre: string | null | undefined): string {
    return nombreBancoLegible(nombre);
  }

  terminacion(numero: string | null | undefined): string {
    return String(numero ?? '').slice(-4) || '****';
  }

  tono(mensaje: Message): ITonoMensajeLx {
    return tonoMensajeLx(mensaje);
  }

  cerrarMensaje(mensaje: Message) {
    this.responseMessage = this.responseMessage.filter(m => m !== mensaje);
  }

  // CLABE con formato «012 180 01234567890 1»: el control guarda solo los dígitos
  // (lo que ya validan y mandan start() y connectAccount).
  alEscribirClabe(evento: Event) {
    const input = evento.target as HTMLInputElement;
    const crudo = input.value;
    const cursor = input.selectionStart ?? crudo.length;
    const anterior = soloDigitos(this.clabeControl.value);
    let digitos = soloDigitos(crudo);
    let antesDelCursor = soloDigitos(crudo.slice(0, cursor)).length;
    // Borró solo un espacio del formato: borra también el dígito de antes.
    if ((evento as InputEvent).inputType === 'deleteContentBackward' && digitos === anterior && antesDelCursor > 0) {
      digitos = digitos.slice(0, antesDelCursor - 1) + digitos.slice(antesDelCursor);
      antesDelCursor--;
    }
    // Como el maxlength="18" de antes: al escribir no pasa de 18.
    if (digitos.length > LONGITUD_CLABE && digitos.length > anterior.length) {
      antesDelCursor = Math.max(0, antesDelCursor - (digitos.length - anterior.length));
      digitos = anterior;
    }
    const formateada = formatearClabe(digitos);
    input.value = formateada;
    if (typeof document !== 'undefined' && document.activeElement === input) {
      const posicion = posicionTrasDigitos(formateada, antesDelCursor);
      input.setSelectionRange(posicion, posicion);
    }
    this.clabeControl.setValue(digitos);
    this.clabeControl.markAsDirty();
  }

  alSalirClabe() {
    this.clabeControl.markAsTouched();
  }

  alSalirRfc() {
    const valor = String(this.rfcControl.value ?? '');
    const limpio = valor.trim().toUpperCase();
    if (limpio !== valor) this.rfcControl.setValue(limpio);
    this.rfcControl.markAsTouched();
  }

  alEnterRfc(evento: Event) {
    evento.preventDefault();
    this.alSalirRfc();
    this.confirmRfcAndContinue();
  }

  alternarBancos() {
    this.mostrarBancos = !this.mostrarBancos;
  }

  alBuscarBanco(evento: Event) {
    this.busquedaBanco = (evento.target as HTMLInputElement).value;
    const buscado = normalizarBusqueda(this.busquedaBanco);
    this.bancosVisibles = buscado
      ? this.bancosLegibles.filter(b => normalizarBusqueda(b).includes(buscado))
      : this.bancosLegibles;
  }

  // CTA «Validar mi cuenta»: la lógica sigue en start(); aquí solo se lleva la
  // vista a lo que falta.
  alValidarCuenta() {
    this.intentoValidar = true;
    this.start();
    if (this.clabeControl.invalid) {
      this.enfocar('#druo-clabe');
    } else if (!this.rfcConfirmed) {
      this.enfocar('#druo-rfc');
    }
  }

  usarCuentaElegida() {
    const cuenta = this.existingAccounts[this.cuentaElegida] ?? this.existingAccounts[0];
    if (cuenta) this.useExistingAccount(cuenta);
  }

  private enfocar(selector: string) {
    const destino = this.host.nativeElement.querySelector<HTMLElement>(selector);
    if (!destino) return;
    const reducido = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    destino.scrollIntoView?.({block: 'center', behavior: reducido ? 'auto' : 'smooth'});
    if (destino instanceof HTMLInputElement) destino.focus({preventScroll: true});
  }
}
